/**
 * @voltxampere/spice — BJT codec (Q)
 *
 * Berkeley SPICE3 syntax:
 *   Q{name} NC NB NE [NS] MODEL_NAME [area]
 *
 * Examples:
 *   Q1 collector base emitter NPN_DEFAULT
 *   Q2 c b e PNP_DEFAULT
 *   Q3 c b e 0 2N3904
 *
 * Polarity dispatch: model name keyword decides — `NPN`, `2N3904`,
 * generic NPN models → `npn`. `PNP`, `2N3906`, generic PNP → `pnp`.
 * Unknown model defaults to `npn` with a warning.
 *
 * Schema mapping: `type: "npn"` or `type: "pnp"` (split symbols),
 * params: `{ model }`. Substrate node optional (3 or 4 nodes).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ok, err, } from "./_base.js";
import { stringParam } from "./_param-helpers.js";
const NPN_MODELS = new Set([
    "NPN",
    "NPN_DEFAULT",
    "NPN_2N3904",
    "2N3904",
    "2N2222",
    "BC547",
    "MMBT3904",
]);
const PNP_MODELS = new Set([
    "PNP",
    "PNP_DEFAULT",
    "PNP_2N3906",
    "2N3906",
    "2N2907",
    "BC557",
    "MMBT3906",
]);
function classifyBjtModel(model) {
    const upper = model.toUpperCase();
    if (PNP_MODELS.has(upper) || upper.startsWith("PNP"))
        return "pnp";
    if (NPN_MODELS.has(upper) || upper.startsWith("NPN"))
        return "npn";
    // 2N390[46] family heuristic.
    if (upper.includes("3906") || upper.includes("2907") || upper.includes("BC557"))
        return "pnp";
    return "npn";
}
export const bjtCodec = {
    spiceLetter: "Q",
    schemaTypes: ["npn", "pnp", "bjt"],
    examples: ["Q1 collector base emitter NPN_DEFAULT", "Q2 c b e PNP_DEFAULT", "Q3 c b e 0 2N3904"],
    parse(card, _ctx) {
        if (card.tokens.length < 4) {
            return err("Q needs at least 3 nodes and a model name");
        }
        // 3 nodes + model = 4 tokens; 4 nodes + model = 5 tokens.
        let nodeCount;
        if (card.tokens.length >= 5 && /^[a-zA-Z_0-9]+$/.test(card.tokens[3] ?? "")) {
            // Could be 4-node form. Heuristic: if the 5th token looks
            // like a known polarity keyword, treat as 4-node.
            const possibleModel = card.tokens[4] ?? "";
            const upper = possibleModel.toUpperCase();
            if (upper.startsWith("NPN") ||
                upper.startsWith("PNP") ||
                NPN_MODELS.has(upper) ||
                PNP_MODELS.has(upper) ||
                upper.includes("3904") ||
                upper.includes("3906")) {
                nodeCount = 4;
            }
            else {
                nodeCount = 3;
            }
        }
        else {
            nodeCount = 3;
        }
        const nodes = card.tokens.slice(0, nodeCount);
        const modelRaw = card.tokens[nodeCount];
        if (modelRaw === undefined)
            return err("Q missing model name");
        const polarity = classifyBjtModel(modelRaw);
        // We only carry the first 3 (C, B, E) into the schema — substrate
        // is dropped from the terminal list (it's almost always ground
        // in real netlists). Keep it in `params` for round-trip.
        const cbeNodes = nodes.slice(0, 3);
        if (cbeNodes.length !== 3) {
            return err("Q needs collector / base / emitter nodes");
        }
        const params = { model: modelRaw };
        if (nodeCount === 4 && nodes[3] !== undefined) {
            params.substrate = nodes[3];
        }
        const component = {
            id: card.identifier,
            type: polarity,
            position: { x: 0, y: 0 },
            rotation: 0,
            flipped: false,
            label: card.identifier,
            params,
            terminals: [],
        };
        // Strip optional 'undefined' (we already validated length === 3).
        const safeNodes = cbeNodes;
        return ok(component, safeNodes);
    },
    emit(component, terminals) {
        const fallback = component.type === "pnp" ? "PNP_DEFAULT" : "NPN_DEFAULT";
        const model = stringParam(component.params.model, fallback);
        const substrate = stringParam(component.params.substrate, "");
        const [c, b, e] = terminals;
        const nodes = `${c ?? "0"} ${b ?? "0"} ${e ?? "0"}`;
        if (substrate.length > 0) {
            return `${component.id} ${nodes} ${substrate} ${model}`;
        }
        return `${component.id} ${nodes} ${model}`;
    },
    owns(schemaType) {
        return schemaType === "npn" || schemaType === "pnp" || schemaType === "bjt";
    },
};
//# sourceMappingURL=bjt.js.map